import React, { useEffect } from 'react';
import { Typography, Icon, Button } from '@scale/scaleui';
import CopyToClipboard from 'react-copy-to-clipboard';
import { ChatBulkAuditBatch, SubtaskAssignment } from '../SpeedAudit/types';
import { AuditLookupType } from '@scale/shared/types/corp/attemptAudit';
import { buildURL } from '@scale/frontend-lib/urls';
import { OUTLIER_FRONTEND_URL } from 'react-scale/lib/constants';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import Kbd from 'react-scale/components/design-system/Kbd';

interface ChatBulkAuditHeaderProps {
  auditBatch: ChatBulkAuditBatch;
  auditorHasClaim: boolean;
  allowEdits: boolean;
  setAllowEdits: (allowEdits: boolean) => void;
  formattedResponseView: boolean;
  setFormattedResponseView: (formattedResponseView: boolean) => void;
  attemptAudit?: SubtaskAssignment;
  resultsPage?: boolean;
  populatedSubtaskAssignment?: SubtaskAssignment;
  setShowFeedbackBar?: (showFeedbackBar: boolean) => void;
}

function ChatBulkAuditHeader(props: ChatBulkAuditHeaderProps) {
  const {
    auditBatch,
    auditorHasClaim,
    allowEdits,
    setAllowEdits,
    attemptAudit,
    formattedResponseView,
    setFormattedResponseView,
    resultsPage,
    populatedSubtaskAssignment,
    setShowFeedbackBar,
  } = props;
  const attemptIdFromLookup =
    attemptAudit?.auditLookup?.type === AuditLookupType.TASK_ATTEMPT
      ? attemptAudit?.auditLookup?.id
      : null;

  const taskId = populatedSubtaskAssignment?.auditedAttempt?.task;
  const handleAllowEdits = () => {
    setAllowEdits(!allowEdits);
  };

  const enableJsonTable = useUnleashFeatureFlag('chat-bulk-audit-json-table', {}, false);

  useEffect(() => {
    if (populatedSubtaskAssignment?.outdated) {
      setAllowEdits(false);
    }
  }, [populatedSubtaskAssignment?.outdated, setAllowEdits]);
  return (
    <div className="bg-neutral-0 px-8 py-3 flex flex-row items-center justify-between border-b border-neutral-200">
      <div className="flex flex-row items-center gap-4">
        <img
          src="https://static.remotasks.com/uploads/602b25a6e0984c00343d3b26/Frame.png"
          alt="Outlier"
          className="w-6 h-6"
        />
        {resultsPage ? (
          <Typography.Subtitle3 className="flex flex-row items-center">
            {auditBatch.name}
          </Typography.Subtitle3>
        ) : (
          <>
            <Typography.Subtitle3 className="flex flex-row items-center">
              {auditBatch.name} / Project {auditBatch.project?.slice(-5)}
              <CopyToClipboard text={auditBatch.project ?? ''}>
                <Icon icon={['far', 'copy']} size="sm" />
              </CopyToClipboard>
              / Attempt{' '}
              {attemptIdFromLookup?.slice(-5) ?? attemptAudit?.auditedAttempt?._id?.slice(-5)}
              <CopyToClipboard
                text={attemptIdFromLookup ?? attemptAudit?.auditedAttempt?._id ?? ''}
              >
                <Icon icon={['far', 'copy']} size="sm" />
              </CopyToClipboard>
              {auditorHasClaim && (
                <>
                  {populatedSubtaskAssignment?.outdated ? (
                    <>
                      {taskId && (
                        <Button
                          className="mx-4"
                          size="sm"
                          onClick={() =>
                            window.open(
                              buildURL(OUTLIER_FRONTEND_URL, '/tasks', {
                                [`taskId`]: taskId,
                                forceClaim: 1,
                              }),
                              '_blank',
                            )
                          }
                        >
                          Claim and View Task on Outlier
                        </Button>
                      )}

                      <Typography.Subtitle3 className="ml-4">
                        The ability to edit this task attempt has been disabled since the params of
                        this task attempt does not match the latest params for the task. If you wish
                        to make edits, please use the force claim link.
                      </Typography.Subtitle3>
                    </>
                  ) : (
                    <Button
                      className="mx-4"
                      variant="secondary"
                      size="sm"
                      onClick={handleAllowEdits}
                    >
                      {allowEdits ? 'Turn off edit mode' : 'Edit Task'}
                    </Button>
                  )}
                </>
              )}
              {enableJsonTable && (
                <Button
                  className="mx-4"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setFormattedResponseView(!formattedResponseView);
                    if (formattedResponseView) {
                      setShowFeedbackBar?.(true);
                    }
                  }}
                >
                  {formattedResponseView ? 'View Chat' : 'View formatted response data'}
                </Button>
              )}
            </Typography.Subtitle3>
          </>
        )}
      </div>
      {!resultsPage && (
        <div className="flex items-center gap-1 ml-4 flex-shrink-0">
          <Typography.Overline3>{'Navigate with'}</Typography.Overline3>
          <Kbd>Cmd</Kbd>
          <Kbd>Shift</Kbd>
          <Kbd>+/-</Kbd>
        </div>
      )}
    </div>
  );
}

export default React.memo(ChatBulkAuditHeader);
